// QA-team registry — FE mirror of clarab/config/qaTeams.js (keep in sync;
// static: 3 teams). This is the TEAM axis of the multi-team split (qa / kick /
// hosts) — do NOT confuse with constants/teams.js TEAMS, which lists CS teams
// (BG I, US I, KYC Team, ...) — the people being graded, not the QA teams.
//
// The server enforces all isolation (teamScope plugin + X-QA-Team header
// validation); everything here is presentation: which pills/markets/tabs the
// UI offers, labels, colors.

import { devPreviewDropsTeamHeader } from '../devRolePreview';

export const QA_TEAMS = {
  qa: {
    key: 'qa',
    label: 'QA',
    short: 'QA',
    color: '#6366f1',
    // Markets this team grades — drives ticketType pills, badges and the
    // grading-dialog type selector. Single-type teams render no pill row.
    // it/dk joined 2026-08-23 — see clarab/config/qaTeams.js for why they are
    // open to every grader and never exported to MaestroQA.
    // latam (Stake MX + Stake AR) joined 2026-08-31 — one market for two
    // countries that share a single Intercom workspace. BE twin:
    // clarab/config/qaTeams.js.
    // ca (Stake Canada — Ontario + Alberta, one workspace) joined 2026-10-11,
    // eu (Stake.eu) on 2026-10-27, poker (the Poker team — Stake.com
    // conversations, set up like Payments) on 2026-10-29.
    ticketTypes: ['com', 'payments', 'us', 'kyc', 'it', 'dk', 'latam', 'br', 'co', 'ca', 'eu', 'poker'],
    defaultTicketType: 'com',
    // Intercom report workspaces the team may browse (team-level clamp; the
    // BE additionally applies the existing role/capability gates within it).
    reportMarkets: ['com', 'us', 'dk', 'mx', 'ar', 'it', 'br', 'co', 'ca', 'eu'],
    // ACP surfaces (relay panel, quick view) exist for this team.
    acp: true,
    // AI Hub (qa-auto picks / orchestrator) — QA-only at launch.
    aiEnabled: true,
  },
  kick: {
    key: 'kick',
    label: 'QA Kick',
    short: 'KICK',
    color: '#53fc18',
    // THREE markets since 2026-08-28 — BE mirror: clarab/config/qaTeams.js.
    // 'kpp' is the Slack-sourced Kick Partner Program moderation flow
    // (#kpp-rule-violations), the kick-workspace analogue of 'kyc' inside 'qa'.
    // 'kickmod' is Kick Moderation QA — the first market with NO upstream
    // conversation: the grader types the ticket in and attaches the evidence.
    // See claraf/src/constants/kickMod.js.
    // 'kps' (Kick Partnership Serbia, 2026-09-10) is the FIRST market with
    // three sources — Intercom, Slack, Discord — one card each. Which one a
    // ticket is comes from its ticketId (constants/kps.js kpsSourceOf).
    ticketTypes: ['kick', 'kpp', 'kickmod', 'kps'],
    defaultTicketType: 'kick',
    reportMarkets: ['kick'],
    acp: false,
    aiEnabled: false,
  },
  hosts: {
    key: 'hosts',
    label: 'QA Hosts',
    short: 'HOSTS',
    color: '#f59e0b',
    ticketTypes: ['hosts'],
    defaultTicketType: 'hosts',
    reportMarkets: ['vip'],
    acp: false,
    aiEnabled: false,
  },
};

export const QA_TEAM_KEYS = Object.keys(QA_TEAMS);
export const DEFAULT_QA_TEAM = 'qa';

// sessionStorage key for the admin acting-team switcher. Deliberately
// session-scoped (per-tab): admins can compare teams side-by-side in two
// tabs, and every fresh tab/login starts back at the default team.
export const ACTING_TEAM_KEY = 'clara_qa_acting_team';

/**
 * Roles that BELONG to one workspace (BE mirror: clarab/config/qaTeams.js
 * ROLE_HOME_TEAM). Since 2026-09-10 kick / hosts graders carry a workspace role
 * instead of being a plain 'qa' moved into the workspace on the Access page.
 */
export const ROLE_HOME_TEAM = {
  'kick-qa': 'kick',
  'kpp-qa': 'kick',
  'kick-mod-qa': 'kick',
  'kps-qa': 'kick',
  'host-qa': 'hosts',
  'host-qa-admin': 'hosts',
};

/** The user's own team — a workspace role decides; else qaTeam; missing/unknown degrades to the incumbent 'qa'. */
export const getQaTeam = (user) => {
  const home = ROLE_HOME_TEAM[user?.role];
  if (home) return home;
  const t = user?.qaTeam;
  return QA_TEAM_KEYS.includes(t) ? t : DEFAULT_QA_TEAM;
};

/** Roles that may act across teams via the switcher / X-QA-Team header. */
export const GLOBAL_TEAM_ROLES = ['admin', 'developer'];
export const isGlobalTeamRole = (user) => GLOBAL_TEAM_ROLES.includes(user?.role);

/**
 * The team the user is ACTING as: their own team, unless they are a global
 * role with a valid per-tab switcher selection. Everything scoped in the UI
 * (pills, dropdown sources, query keys, storage keys) keys off this.
 */
export const getActingQaTeam = (user) => {
  if (!isGlobalTeamRole(user)) return getQaTeam(user);
  // Rollback guard: enforcement off/shadow hides both the switcher and the
  // acting-as chip, so a leftover selection must not keep scoping the UI with
  // no way out. Strict === false — an absent flag (older backend, or the user
  // object from login before the first profile fetch) keeps the stored
  // selection, so nothing changes while enforcement is on.
  if (user?.qaTeamEnforcementActive === false) return getQaTeam(user);
  try {
    const v = sessionStorage.getItem(ACTING_TEAM_KEY);
    return QA_TEAM_KEYS.includes(v) ? v : DEFAULT_QA_TEAM;
  } catch {
    return DEFAULT_QA_TEAM;
  }
};

/**
 * The stored acting-team value that may travel as the X-QA-Team header, or null
 * when nothing should be sent. Single source of truth for the axios interceptor
 * (index.js) and the raw-fetch call sites that bypass it (QAManagerLayout.jsx,
 * QAExport.jsx) — a new team added to QA_TEAMS becomes forwardable everywhere
 * at once instead of silently falling back to qa-scoped data.
 * Only a real non-default team qualifies: absent header = act as own team, and
 * any other/stale value is dropped because the backend 403s this header for
 * non-admin/developer roles (which would break every QA request in the tab).
 */
export const actingTeamHeaderValue = () => {
  try {
    // Local-dev role preview: the backend 403s this header for anything but
    // admin/developer, so previewing as (say) 'tl' with a kick/hosts selection
    // still in this tab would 403 EVERY qa-manager request — which reads as
    // "the preview is broken" rather than "you are still acting as kick".
    // A previewed non-global role acts as its own team, same as the real one.
    if (devPreviewDropsTeamHeader()) return null;
    const v = sessionStorage.getItem(ACTING_TEAM_KEY);
    return QA_TEAM_KEYS.includes(v) && v !== DEFAULT_QA_TEAM ? v : null;
  } catch {
    return null; // sessionStorage unavailable — act as own team
  }
};

export const getTeamConfig = (teamKey) => QA_TEAMS[teamKey] || QA_TEAMS[DEFAULT_QA_TEAM];

/**
 * Markets that have ALWAYS been rubric-only — they never had a hardcoded
 * scorecard and never round-tripped through MaestroQA. FE mirror of
 * clarab/config/qaTeams.js RUBRIC_ENABLED_MARKETS, which is equally static.
 *
 * This is the PRE-BOOT FALLBACK, not the live answer. The live answer comes
 * from the server (see setRubricMarkets below) because the Maestro cutover is
 * an env flag on the backend: flipping it must change grading behaviour with
 * NO frontend deploy. Never gate grading on this constant directly — call
 * isRubricMarket().
 */
export const RUBRIC_ENABLED_MARKETS = ['kyc', 'kick', 'hosts', 'kpp', 'kickmod', 'kps'];

/**
 * Markets whose grading form has the Reoccurring Error field: every CS market
 * (com/us/payments/poker…, rubric or not), and Hosts since 2026-11-03 (its
 * graders mark it by hand; the list is the workspace's own, Active Overview →
 * Categories). kyc and the Kick markets never had it. The STATIC rubric list on
 * purpose — the cutover-aware one would drop the field from the CS markets.
 */
export const hasReoccurringError = (market) => !RUBRIC_ENABLED_MARKETS.includes(market) || market === 'hosts';

// Live copy, replaced once GET /api/rubrics/assigned answers. Module-level on
// purpose: isRubricMarket() is called from render paths and plain helpers all
// over the QA dialogs, and threading server state through every one of them
// would be a far larger change than the cutover itself. Same shape as
// actingTeamHeaderValue(), which likewise reads ambient state at call time.
let _rubricMarkets = RUBRIC_ENABLED_MARKETS;
let _cutoverDone = false;

/**
 * Adopt the server's view. Called once from QAManagerContext when
 * /api/rubrics/assigned responds; a failed fetch leaves the static fallback in
 * place, which is the pre-cutover behaviour — i.e. it fails CLOSED.
 */
export const setRubricMarkets = (markets, cutoverDone) => {
  if (Array.isArray(markets) && markets.length) _rubricMarkets = markets;
  _cutoverDone = cutoverDone === true;
};

/** Markets gradable on a builder rubric AND released internally, right now. */
export const getRubricMarkets = () => _rubricMarkets;

/** True once MaestroQA is out of the loop — nothing may be exported to it. */
export const isCutoverDone = () => _cutoverDone;

export const isRubricMarket = (ticketType) => _rubricMarkets.includes(ticketType || 'com');

/**
 * Is this rubric untouchable right now? FE twin of the backend
 * rubricsController.isLockedRubric — keep the two identical or the UI offers a
 * button the server then 403s.
 *
 *  - `kind: 'notes'` is permanent.
 *  - `isSystem` is locked only until the Maestro cutover. After the flip the
 *    seeded mirrors become ordinary rubrics so they can be retired in favour of
 *    the `CS scorecard | Official` set — that is the point of the cutover.
 *
 * PASS `cutoverDone` EXPLICITLY from render paths. The module-level copy below
 * is set by a fetch callback, and a module variable changing does not re-render
 * anything — a component that read it during its first render stayed locked
 * forever, which is exactly how the builder showed "locked until cutover" on a
 * production box that had already flipped. Event handlers may rely on the
 * default (they read at click time, long after the fetch).
 * Fails CLOSED either way: unknown state = still locked.
 */
export const isRubricLocked = (rubric, cutoverDone = isCutoverDone()) =>
  !!rubric && (rubric.kind === 'notes' || (!!rubric.isSystem && !cutoverDone));

/** System rubrics are never deletable, in either phase — retire them instead. */
export const isRubricDeletable = (rubric) => !!rubric && !rubric.isSystem && rubric.kind !== 'notes';

/**
 * Intercom workspace for a ticketType — the default `?market=` for the
 * conversation viewer / customer-lookup panels when the caller passes no
 * explicit market prop. FE twin of the BE registry pair
 * (clarab/config/qaTeams.js intercomMarkets + config/intercomMarkets.js, whose
 * intercomMarketForTicketType() is the byte-identical BE twin of this map):
 * kick tickets live in the Kick.com workspace, hosts tickets in VIP Hosts,
 * us/it/dk each keep their own workspace, and everything else (com/payments/kyc
 * and legacy tickets with no ticketType) stays on com.
 */
// 'latam' resolves to the 'mx' workspace key — mx and ar are one Intercom
// workspace (wzz0yzot) behind one token, so the single QA market points at
// whichever of the two keys carries it. BE twin: intercomMarketForTicketType().
const TICKET_TYPE_TO_MARKET = { us: 'us', kick: 'kick', kps: 'kick', hosts: 'vip', it: 'it', dk: 'dk', latam: 'mx', br: 'br', co: 'co', ca: 'ca', eu: 'eu' };
export const marketForTicketType = (ticketType) => TICKET_TYPE_TO_MARKET[ticketType] || 'com';

/**
 * CS-team option source per QA team — which "teams of graded people" a QA
 * team's dropdowns offer. For 'qa' the caller should use the existing
 * constants/teams.js TEAMS list (kept there to avoid duplicating 42 names);
 * kick's five sub-teams and the hosts bucket live here.
 */
export const KICK_CS_TEAMS = [
  'Kick General',
  'Kick Live Chat',
  // The three numbered Live Chat sub-teams are what agents ACTUALLY carry
  // (7 / 7 / 6 docs in prod, verified 2026-08-26) — the bare 'Kick Live Chat'
  // above holds none. They were missing here, so the kick dropdowns offered
  // only names nobody has and an existing agent's real team survived purely
  // because teamOptionsWith() prepends an unknown current value.
  'Kick Live Chat Team I',
  'Kick Live Chat Team II',
  'Kick Live Chat Team III',
  // The KPP moderation roster (28 docs) — the 'kpp' market's CS team.
  'Kick Partners',
  // The three KPP shifts (2026-09-21) — BE twin: clarab/utils/tlStats.js BG_TEAMS.
  'KPP I',
  'KPP II',
  'KPP III',
  // The Kick Moderation roster (62 docs) — the 'kickmod' market's CS team.
  // BE twin: clarab/utils/tlStats.js BG_TEAMS, which is what actually gates
  // /tl access (canReadAgent intersects the agent's team with that list).
  'Kick Moderation',
  // The three moderation shifts (2026-09-18) — the roster split by shift lead.
  // BE twin: clarab/utils/tlStats.js BG_TEAMS. 'Kick Moderation' stays for the
  // moderators no shift lists yet.
  "Kick Mod Filip's Shift",
  "Kick Mod Marko's Shift",
  "Kick Mod Veljko's Shift",
  // The Kick Partnership Serbia roster — the 'kps' market's CS team (2026-09-10).
  'Kick Partnership Serbia',
  // The seven KPS shifts (2026-09-24) — the roster split by shift lead.
  // BE twin: clarab/utils/tlStats.js BG_TEAMS. 'Kick Partnership Serbia' stays
  // for anyone no shift lists.
  "KPS Souha's Shift",
  "KPS Yüksel's Shift",
  "KPS Mabruka's Shift",
  "KPS Maria's Shift",
  "KPS Miloš's Shift",
  "KPS Nemanja's Shift",
  "KPS Petar's Shift",
  'Kick Verify',
  'Kick App Support',
];
// The hosts workspace's CS teams — one per VIP roster cohort (what
// /vip-host-metrics shows). BE twin: clarab/utils/tlStats.js BG_TEAMS. Until
// 2026-10-19 this offered only 'VIP Hosts', a team no host carries, so the
// People filter and the bulk / edit dropdowns never named the real teams.
export const HOSTS_CS_TEAMS = ['Regular Hosts', 'Backup Hosts', 'Secondary Hosts'];

/**
 * Display label per market. ONE definition — GradesList and ScoreDetailDrawer
 * each carried their own copy and they had already drifted (the list was
 * missing kick/hosts, so those rendered as raw lowercase strings).
 */
export const MARKET_LABELS = { com: 'Com', us: 'US', payments: 'Payments', kyc: 'KYC', it: 'IT', dk: 'DK', latam: 'LATAM', br: 'BR', co: 'CO', ca: 'CA', eu: 'EU', poker: 'Poker', kick: 'Kick', kpp: 'KPP', kickmod: 'Kick Mod', kps: 'KPS', hosts: 'Hosts' };
export const marketLabel = (m) => MARKET_LABELS[m] || m || '—';

/**
 * Ticket-type filter pills, keyed by market. ONE definition — QAArchive and
 * QATickets each carried a byte-identical copy and ArchiveSearchPanel a third,
 * shorter one that had already drifted (com/payments/us only, so an it/dk/kyc
 * filter rendered with the US label).
 *
 * `activeCls` is the colour a market is known by across the whole QA UI —
 * TicketTypeBadge uses the same families so a pill and a badge for the same
 * market never disagree. A new market needs an entry HERE, an arm in
 * TicketTypeBadge, and nothing else.
 *
 * Callers filter this by the acting team's `ticketTypes`, so a team only ever
 * sees pills for markets it actually grades.
 */
export const TYPE_PILL_OPTIONS = {
  com:      { value: 'com',      label: 'Com',      activeCls: 'bg-gray-700 dark:bg-neutral-600 text-white' },
  payments: { value: 'payments', label: 'Payments', activeCls: 'bg-indigo-600 text-white' },
  us:       { value: 'us',       label: 'US',       activeCls: 'bg-red-600 text-white' },
  kyc:      { value: 'kyc',      label: 'KYC',      activeCls: 'bg-emerald-600 text-white' },
  it:       { value: 'it',       label: 'IT',       activeCls: 'bg-green-700 text-white' },
  dk:       { value: 'dk',       label: 'DK',       activeCls: 'bg-rose-700 text-white' },
  latam:    { value: 'latam',    label: 'LATAM',    activeCls: 'bg-sky-600 text-white' },
  br:       { value: 'br',       label: 'BR',       activeCls: 'bg-yellow-600 text-white' },
  co:       { value: 'co',       label: 'CO',       activeCls: 'bg-orange-600 text-white' },
  ca:       { value: 'ca',       label: 'CA',       activeCls: 'bg-pink-600 text-white' },
  eu:       { value: 'eu',       label: 'EU',       activeCls: 'bg-blue-600 text-white' },
  poker:    { value: 'poker',    label: 'Poker',    activeCls: 'bg-purple-600 text-white' },
  kick:     { value: 'kick',     label: 'Kick',     activeCls: 'bg-lime-600 text-white' },
  kpp:      { value: 'kpp',      label: 'KPP',      activeCls: 'bg-teal-600 text-white' },
  kickmod:  { value: 'kickmod',  label: 'Kick Mod', activeCls: 'bg-fuchsia-600 text-white' },
  kps:      { value: 'kps',      label: 'KPS',      activeCls: 'bg-violet-600 text-white' },
  hosts:    { value: 'hosts',    label: 'Hosts',    activeCls: 'bg-amber-600 text-white' },
};

/**
 * The 1px accent bar the ticket dialogs paint above their header, per market.
 * 'com' has none by design (it is the default and the lists are full of it).
 *
 * This replaces a `payments ? indigo : kyc ? emerald : red` chain that lived in
 * three dialogs: every market it did not name fell through to the US red, so
 * it/dk/kick/hosts tickets opened wearing another market's colour. A map has
 * no else branch to fall through.
 */
export const MARKET_ACCENT_GRADIENT = {
  payments: 'bg-gradient-to-r from-indigo-400 to-indigo-600',
  us:       'bg-gradient-to-r from-red-400 to-red-600',
  kyc:      'bg-gradient-to-r from-emerald-400 to-emerald-600',
  it:       'bg-gradient-to-r from-green-400 to-green-600',
  dk:       'bg-gradient-to-r from-rose-400 to-rose-600',
  latam:    'bg-gradient-to-r from-sky-400 to-sky-600',
  br:       'bg-gradient-to-r from-yellow-400 to-yellow-600',
  co:       'bg-gradient-to-r from-orange-400 to-orange-600',
  ca:       'bg-gradient-to-r from-pink-400 to-pink-600',
  eu:       'bg-gradient-to-r from-blue-400 to-blue-600',
  poker:    'bg-gradient-to-r from-purple-400 to-purple-600',
  kick:     'bg-gradient-to-r from-lime-400 to-lime-600',
  kpp:      'bg-gradient-to-r from-teal-400 to-teal-600',
  kickmod:  'bg-gradient-to-r from-fuchsia-400 to-fuchsia-600',
  kps:      'bg-gradient-to-r from-violet-400 to-violet-600',
  hosts:    'bg-gradient-to-r from-amber-400 to-amber-600',
};
